import type { FreshaGraphQLBooking } from '@/types/fresha';
import type { Appointment, AppointmentStatus, SortField } from '@/types/appointments';
import type { LocationNameMode } from '@/types/settings';
import { buildLocationMap } from './locationResolution';

export function consolidateBookings(
  bookings: FreshaGraphQLBooking[],
  employees: any[] = [],
  locations: any[] = [],
  locationAliases: Record<string, string> = {},
  locationNameMode: LocationNameMode = 'alias'
): Appointment[] {
  const empMap = new Map<string, string>();
  for (const emp of employees) {
    const name = emp?.attributes?.name || `${emp?.attributes?.['first-name'] || ''} ${emp?.attributes?.['last-name'] || ''}`.trim();
    if (emp?.id && name) empMap.set(String(emp.id), name);
  }

  const locMap = buildLocationMap(locations, locationAliases, locationNameMode);

  // Group by appointmentId if present, or fallback to booking id
  const groups = new Map<string, FreshaGraphQLBooking[]>();
  for (const b of bookings) {
    const key = b.appointmentId ? String(b.appointmentId) : String(b.id);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(b);
  }

  const result: Appointment[] = [];

  for (const [, items] of groups.entries()) {
    // Primary booking is the one without parentId (root booking), or the first one
    const primary = items.find((i) => !i.parentId || i.parentId === '') || items[0];
    const primaryBookingId = String(primary.id);
    const bookingIds = items.map((i) => String(i.id));

    const services = items.map((i) => i.serviceName).filter(Boolean);
    const serviceName = services.length > 0 ? services.join(' + ') : (primary.serviceName || 'Servicio');

    const totalBasePrice = items.reduce((sum, i) => sum + (Number(i.basePrice) || 0), 0);
    const totalPrice = items.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
    const totalAmount = totalPrice > 0 ? totalPrice : totalBasePrice;

    const allNotes = items.map((i) => i.notes).filter(Boolean);
    const notes = allNotes.length > 0 ? allNotes.join(' | ') : undefined;

    const employeeName = empMap.get(String(primary.employeeId)) || undefined;
    const locationName = locMap.get(String(primary.locationId)) || undefined;

    result.push({
      id: primary.appointmentId ? String(primary.appointmentId) : primaryBookingId,
      appointmentId: primary.appointmentId ? String(primary.appointmentId) : primaryBookingId,
      primaryBookingId,
      bookingIds,
      customerId: primary.customerId,
      customerName: primary.customerName || 'Cliente sin nombre',
      serviceName,
      startTime: primary.startTime || '00:00:00',
      endTime: primary.endTime || primary.startTime || '00:00:00',
      date: primary.date || '',
      status: (primary.status?.toLowerCase().replace('_', '-') || 'new') as AppointmentStatus,
      employeeId: primary.employeeId,
      employeeName,
      locationId: primary.locationId,
      locationName,
      notes,
      price: totalPrice,
      totalAmount,
      salePaymentStatus: primary.salePaymentStatus,
      salesOrderId: primary.salesOrderId,
    });
  }

  return result;
}

export function getFilteredItems(
  items: Appointment[],
  targetDate: string,
  targetLocationId: string | null,
  query: string,
  status: string,
  visibleStatuses: AppointmentStatus[] = [],
  filterEmployee: string = 'all',
  filterLocation: string = 'all'
): Appointment[] {
  // Strict Context Veracity: Never show appointments of date X under date Y
  let filtered = items.filter((a) => !a.date || a.date === targetDate);

  // Strict Location Veracity
  if (targetLocationId && targetLocationId !== 'all') {
    filtered = filtered.filter((a) => !a.locationId || String(a.locationId) === String(targetLocationId));
  }

  // Quick In-Memory Location Filter
  if (filterLocation && filterLocation !== 'all') {
    filtered = filtered.filter((a) => !a.locationId || String(a.locationId) === String(filterLocation));
  }

  // Quick In-Memory Staff Filter
  if (filterEmployee && filterEmployee !== 'all') {
    filtered = filtered.filter((a) => a.employeeId === filterEmployee || a.employeeName === filterEmployee);
  }

  if (visibleStatuses && visibleStatuses.length > 0) {
    filtered = filtered.filter(
      (a) => visibleStatuses.includes(a.status) || a.status === 'unconfirmed' || a.status === 'confirmed'
    );
  }

  if (status && status !== 'all') {
    filtered = filtered.filter((a) => a.status === status);
  }

  if (query) {
    const q = query.toLowerCase();
    filtered = filtered.filter((a) =>
      a.customerName.toLowerCase().includes(q) ||
      a.serviceName.toLowerCase().includes(q) ||
      a.employeeName?.toLowerCase().includes(q) ||
      a.customerPhone?.includes(q)
    );
  }

  return filtered;
}

export function getSortedItems(
  items: Appointment[],
  sortBy: SortField,
  sortOrder: string
): Appointment[] {
  const sorted = [...items].sort((a, b) => {
    let cmp = 0;
    switch (sortBy) {
      case 'time':
        cmp = a.startTime.localeCompare(b.startTime);
        break;
      case 'name':
        cmp = a.customerName.localeCompare(b.customerName);
        break;
      case 'status':
        cmp = a.status.localeCompare(b.status);
        break;
      default:
        cmp = a.startTime.localeCompare(b.startTime);
    }
    return sortOrder === 'desc' ? -cmp : cmp;
  });
  return sorted;
}

export function getGroupedItems(
  items: Appointment[],
  groupBy: string
): Record<string, Appointment[]> {
  if (groupBy === 'none') return { '': items };

  const groups: Record<string, Appointment[]> = {};
  for (const item of items) {
    let key = '';
    switch (groupBy) {
      case 'status':
        key = item.status;
        break;
      case 'employee':
        key = item.employeeName || 'Sin asignar';
        break;
      case 'date':
        key = item.date;
        break;
      case 'time':
        key = item.startTime ? item.startTime.substring(0, 5) : 'Sin hora';
        break;
      default:
        key = '';
    }
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  return groups;
}

